import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useOrdersList from '@hooks/useOrdersList'
import { generateUrls } from '@hooks/useOrder'
import useServices from '@hooks/useServices'
import useStatuses from '@hooks/useStatuses'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'
import NotificationBannerLocation from '@components/NotificationBannerLocation'

import { ServiceModel, StatusModel } from '@models/types'

import OrderListItem from '../OrderListItem'
import OrderTools from '../OrderTools'

type BuildPageFiltersParams = {
  allowedFilters: string[],
  statuses: StatusModel[],
  services: ServiceModel[],
}

const buildPageFilterFields = (params: BuildPageFiltersParams) => {
  const { allowedFilters, statuses, services } = params

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
      hidden: !allowedFilters.includes('string'),
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      key: 'agency_id',
      hidden: !allowedFilters.includes('agency_id'),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'status_id',
      hidden: !allowedFilters.includes('status_id'),
      label: 'Status',
      values: statuses.map(status => ({
        key: status.id,
        label: status.title,
      })),
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
      },
      key: 'publication_id',
      hidden: !allowedFilters.includes('publication_id'),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      hidden: !allowedFilters.includes('service_id'),
      label: 'Service',
      values: services?.map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
  ]
}

interface PageContentProps {
  currentUserPayload: ReturnType<typeof useCurrentUser>,
  pageFilters : {
    [key: string]: any,
  },

}

const PageContent = (props: PageContentProps) => {
  const { currentUserPayload, pageFilters } = props

  const { filteredServices } = useServices()

  const ordersPayload = useOrdersList({
    filters: pageFilters,
    performHttpRequests: true,
    requestOptions: {
      // @ts-expect-error TS2353
      include_agency_provider_account_for_provider: true,
      include_order_agency: true,
      include_order_campaign: true,
      include_order_item_artwork: true,
      include_order_items: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredOrders,
    filteredOrdersCount,
    hasFilteredOrders,
    loading: loadingOrders,
  } = ordersPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { orderIndexUrl } = generateUrls()
  const { isBrandUser } = currentUserPayload

  const statusKeys = isBrandUser ? ['approved', 'awaiting_review', 'booked', 'pending'] : []
  const { filteredStatuses, loading: loadingStatuses } = useStatuses({ filters: { status_keys: statusKeys } })

  const filterFieldParams = {
    allowedFilters: ['agency_id', 'string', 'status_id'],
    statuses: [{ id: 'current', title: 'Current' }, ...filteredStatuses],
  }

  const brandFieldParams = {
    allowedFilters: ['agency_id', 'publication_id', 'service_id', 'status_id', 'string'],
    statuses: [...filteredStatuses],
    services: filteredServices,
  }

  const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

  const loading = loadingOrders || loadingStatuses

  return (
    <>
      <Helmet>
        <title>Orders | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={redirect && (
          <Button
            // @ts-expect-error TS2322
            as="a"
            buttonStyle="secondaryUtility"
            href={redirect}
            size="medium"
            width="auto"
          >
            Back
          </Button>
        )}
        activeTabBarItemKey="orders"
        tabBarItems={[
          {
            href: orderIndexUrl,
            icon: faGlobe,
            key: 'orders',
            title: 'Orders',
          },
        ]}
        title="Orders"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="provider.orders.dashboard.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredOrders && <ListBlankState />}

            {!loading && hasFilteredOrders && (
              <>
                <Box flexDirection="column">
                  {filteredOrders.map(({ order, orderItemIds }) => (
                    <OrderListItem
                      currentUserPayload={currentUserPayload}
                      key={order.id}
                      order={order}
                      orderItemIds={orderItemIds}
                    />
                  ))}
                </Box>

                <Box variant="white">
                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredOrdersCount}
                    loading={loading}
                    showBorderTop={false}
                  />
                </Box>
              </>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(isBrandUser ? brandFieldParams : filterFieldParams)}
              pageFilters={pageFilters}
              title="Filters"
            />

            <OrderTools />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
